import { buildServiceSearchText, normalizeText, resolveServiceCatalogEntry } from './serviceResolver';
import { normalizeService } from '../catalog/normalizedServiceSchema';

export const SERVICE_PRICING_CONFIGS = [
  {
    key: 'satyanarayan-flex-pricing',
    matchers: ['satyanarayan', 'satynarayan'],
    platformFee: 99,
    dakshina: {
      min: 999,
      max: 2999,
      step: 100,
      defaultValue: 1499
    },
    packageLabel: 'Ritual Dakshina',
    packageDescription: 'Choose dakshina as per your comfort while keeping pricing transparent.',
    platformFeeNote: 'Platform fee covers booking support, provider coordination, and customer assistance.',
    execution: {
      pandits: '1-2 pandits',
      duration: '2 to 2.5 hours'
    },
    addOns: [
      { id: 'samagri-complete', name: 'Complete Pooja Samagri', description: 'All required samagri delivered before ceremony.', price: 699 },
      { id: 'prasad-kit', name: 'Prasad Preparation Kit', description: 'Curated ingredients and setup support for prasad offering.', price: 399 },
      { id: 'extra-pandit', name: 'Additional Pandit Support', description: 'Add one more pandit for larger family participation.', price: 799 }
    ]
  },
  {
    key: 'griha-pravesh-flex-pricing',
    matchers: ['griha pravesh', 'griha-pravesh', 'grah pravesh'],
    platformFee: 99,
    schedulePricing: {
      mode: 'per-pandit-per-day',
      ratePerPanditPerDay: 2499,
      dayOptions: [1, 2, 3, 5, 7, 9],
      defaultDays: 1,
      panditCountByDays: {
        1: { min: 1, max: 2, defaultValue: 1 },
        2: { min: 2, max: 3, defaultValue: 2 },
        3: { min: 2, max: 4, defaultValue: 2 },
        5: { min: 3, max: 5, defaultValue: 3 },
        7: { min: 4, max: 6, defaultValue: 4 },
        9: { min: 5, max: 7, defaultValue: 5 }
      }
    },
    packageLabel: 'Griha Pravesh Ritual Charges',
    packageDescription: 'Charges are based on number of days and pandit team size.',
    platformFeeNote: 'Platform fee covers slot confirmation, support, and provider coordination.',
    execution: { pandits: '1-2 pandits', duration: '2.5 to 3.5 hours' },
    addOns: [
      { id: 'samagri-griha', name: 'Premium Samagri Kit', description: 'Curated samagri for full griha pravesh rituals.', price: 899 },
      { id: 'vastu-shanti', name: 'Vastu Shanti Pooja', description: 'Traditional vastu shanti ritual performed during griha pravesh.', price: 1299 },
      { id: 'satchandi-havan', name: 'Satchandi Havan', description: 'Special havan for deeper spiritual cleansing and blessings.', price: 2499 },
      { id: 'jaap-seva', name: 'Additional Jaap Seva', description: 'Extended jaap support by pandit team as per family tradition.', price: 999 },
      { id: 'satyanarayan-puja-addon', name: 'Satyanarayan Puja Add-on', description: 'Include full Satyanarayan Puja along with griha pravesh.', price: 1499 }
    ]
  },
  {
    key: 'rudrabhishek-flex-pricing',
    matchers: ['rudrabhishek', 'rudra abhishek'],
    platformFee: 149,
    dakshina: { min: 1999, max: 6999, step: 100, defaultValue: 2999 },
    packageLabel: 'Rudrabhishek Dakshina',
    packageDescription: 'Select dakshina amount with complete pricing clarity.',
    platformFeeNote: 'Platform fee includes booking support and ceremony coordination assistance.',
    execution: { pandits: '2 pandits', duration: '3 to 4 hours' },
    addOns: [
      { id: 'abhishek-dravya', name: 'Abhishek Dravya Set', description: 'Complete abhishek dravya and ritual essentials.', price: 1099 },
      { id: 'mahamrityunjay-jaap', name: 'Mahamrityunjay Jaap', description: 'Extended jaap support after main ritual.', price: 1299 }
    ],
    detailedInfo: {
      highlights: [
        'Rudra abhishek with Vedic mantra sequence and sankalp.',
        'Can be combined with Mahamrityunjay Jaap based on family preference.',
        'Samagri and setup support available through add-ons.'
      ]
    }
  },
  {
    key: 'mahamrityunjay-jaap-pricing',
    matchers: ['mahamrityunjay', 'maha mrityunjay', 'mrityunjay jaap'],
    platformFee: 149,
    jaapPricing: {
      mode: 'jaap-count',
      defaultCount: 51000,
      options: [
        { count: 31000, label: '31,000 Jaap', price: 18999, pandits: '3-4 pandits', duration: '1 day' },
        { count: 51000, label: '51,000 Jaap', price: 29999, pandits: '5-6 pandits', duration: '1-2 days' },
        { count: 100000, label: '1,00,000 Jaap', price: 54999, pandits: '7-9 pandits', duration: '2-3 days' },
        { count: 125000, label: '1,25,000+ Jaap', price: 67999, pandits: '9-11 pandits', duration: '3+ days' }
      ]
    },
    packageLabel: 'Mahamrityunjay Jaap Charges',
    packageDescription: 'Select jaap count and see transparent pricing and execution details.',
    platformFeeNote: 'Platform fee includes booking support, tracking, and team coordination.',
    addOns: [
      { id: 'jaap-samagri-premium', name: 'Premium Jaap Samagri Kit', description: 'Complete materials for extended jaap and havan process.', price: 1499 },
      { id: 'recording-and-report', name: 'Jaap Completion Report', description: 'Structured completion summary with sankalp details.', price: 799 }
    ],
    detailedInfo: {
      highlights: [
        'Jaap count options available: 31k, 51k, 1 lakh and above.',
        'Price varies by jaap count, team size, and duration.',
        'Final timeline is confirmed after sankalp and slot planning.'
      ],
      note: 'For counts above 1.25 lakh, our team shares a custom quote after requirement review.'
    }
  },
  {
    key: 'satchandi-havan-pricing',
    matchers: ['satchandi', 'sat chandi', 'chandi havan'],
    platformFee: 199,
    anusthanPricing: {
      mode: 'anusthan-pandit',
      defaultType: 'laghu',
      types: [
        { key: 'laghu', label: 'Laghu Anusthan', basePricePerPandit: 2999, minPandits: 2, maxPandits: 5, defaultPandits: 2, duration: '1 day' },
        { key: 'madhyam', label: 'Madhyam Anusthan', basePricePerPandit: 4499, minPandits: 4, maxPandits: 7, defaultPandits: 4, duration: '2 days' },
        { key: 'maha', label: 'Maha Anusthan', basePricePerPandit: 6499, minPandits: 7, maxPandits: 11, defaultPandits: 7, duration: '3+ days' }
      ]
    },
    packageLabel: 'Satchandi Anusthan Charges',
    packageDescription: 'Pricing varies by anusthan type and pandit team size.',
    platformFeeNote: 'Platform fee includes planning support, ritual scheduling, and assistance desk.',
    addOns: [
      { id: 'chandi-path-samagri', name: 'Chandi Path Samagri', description: 'Complete samagri for satchandi havan and related rituals.', price: 1999 },
      { id: 'extended-purnahuti', name: 'Extended Purnahuti Setup', description: 'Expanded setup for larger family participation.', price: 1499 }
    ],
    detailedInfo: {
      highlights: [
        'Anusthan options: Laghu, Madhyam, and Maha.',
        'Pricing is calculated from selected type and pandit count.',
        'Timeline and setup vary by anusthan complexity.'
      ],
      note: 'Custom anusthan plans can be arranged for temple or community events.'
    }
  },
  {
    key: 'grah-shanti-pricing',
    matchers: ['grah shanti', 'graha shanti', 'navgraha shanti'],
    platformFee: 99,
    dakshina: { min: 1499, max: 5999, step: 100, defaultValue: 2499 },
    packageLabel: 'Grah Shanti Charges',
    packageDescription: 'Flexible pricing based on ritual depth and family requirements.',
    platformFeeNote: 'Platform fee includes booking support and pandit coordination.',
    execution: { pandits: '1-3 pandits', duration: '2 to 4 hours' },
    addOns: [
      { id: 'navgraha-jap-addon', name: 'Navgraha Jaap Add-on', description: 'Extended jaap for all grahas based on sankalp.', price: 1299 },
      { id: 'grah-shanti-samagri', name: 'Complete Grah Shanti Samagri', description: 'All required samagri arranged and delivered.', price: 899 }
    ],
    detailedInfo: {
      highlights: [
        'Ideal for graha dosha balancing and peace rituals.',
        'Supports custom sankalp and family-specific requests.',
        'Can be combined with havan and jaap add-ons.'
      ]
    }
  },
  {
    key: 'bhagwat-katha-pricing',
    matchers: ['bhagwat katha', 'bhagavat katha', 'bhagwat'],
    platformFee: 149,
    schedulePricing: {
      mode: 'per-pandit-per-day',
      ratePerPanditPerDay: 3499,
      dayOptions: [1, 3, 5, 7],
      defaultDays: 3,
      panditCountByDays: {
        1: { min: 2, max: 4, defaultValue: 2 },
        3: { min: 3, max: 6, defaultValue: 3 },
        5: { min: 5, max: 8, defaultValue: 5 },
        7: { min: 7, max: 11, defaultValue: 7 }
      }
    },
    packageLabel: 'Bhagwat Katha Charges',
    packageDescription: 'Pricing depends on duration and pandit team size.',
    platformFeeNote: 'Platform fee includes schedule planning and support desk.',
    execution: { pandits: '3-11 pandits', duration: '1 to 7 days' },
    addOns: [{ id: 'katha-stage-setup', name: 'Katha Stage Setup', description: 'Basic stage and seating setup for katha sessions.', price: 2499 }],
    detailedInfo: {
      highlights: [
        'Available in 1, 3, 5, and 7-day formats.',
        'Pandit team scales with duration and audience size.',
        'Transparent per-pandit/day pricing model.'
      ]
    }
  },
  {
    key: 'ramayan-katha-pricing',
    matchers: ['ramayan katha', 'ram katha'],
    platformFee: 149,
    schedulePricing: {
      mode: 'per-pandit-per-day',
      ratePerPanditPerDay: 2999,
      dayOptions: [1, 3, 5, 7],
      defaultDays: 3,
      panditCountByDays: {
        1: { min: 2, max: 4, defaultValue: 2 },
        3: { min: 3, max: 6, defaultValue: 3 },
        5: { min: 5, max: 8, defaultValue: 5 },
        7: { min: 7, max: 11, defaultValue: 7 }
      }
    },
    packageLabel: 'Ramayan Katha Charges',
    packageDescription: 'Select duration and pandit team for complete ramayan katha.',
    platformFeeNote: 'Platform fee includes session planning and support coordination.',
    execution: { pandits: '2-11 pandits', duration: '1 to 7 days' },
    addOns: [{ id: 'bhajan-mandali', name: 'Bhajan Mandali Add-on', description: 'Live devotional support during katha.', price: 1999 }],
    detailedInfo: {
      highlights: [
        'Structured katha flow with experienced pathak team.',
        'Duration and team size are fully configurable.',
        'Suitable for family and community-level events.'
      ]
    }
  },
  {
    key: 'sundar-kand-path-pricing',
    matchers: ['sundar kand', 'sunder kand', 'sundarkand'],
    platformFee: 99,
    dakshina: { min: 999, max: 3999, step: 100, defaultValue: 1999 },
    packageLabel: 'Sundar Kand Path Charges',
    packageDescription: 'Flexible dakshina for path session and sankalp support.',
    platformFeeNote: 'Platform fee includes booking and coordination assistance.',
    execution: { pandits: '1-3 pandits', duration: '2 to 4 hours' },
    addOns: [{ id: 'hanuman-chalisa-jaap', name: 'Hanuman Chalisa Jaap Add-on', description: 'Extended chalisa jaap after sundar kand path.', price: 699 }],
    detailedInfo: {
      highlights: [
        'Ideal for protection and positivity-focused sankalp.',
        'Short and long path formats available.',
        'Can include additional jaap and havan options.'
      ]
    }
  },
  {
    key: 'shravan-anushtan-pricing',
    matchers: ['shravan puja', 'shravan anushtan', 'sawan puja', 'sawan anushtan'],
    platformFee: 149,
    anusthanPricing: {
      mode: 'anusthan-pandit',
      defaultType: 'madhyam',
      types: [
        { key: 'laghu', label: 'Laghu Anusthan', basePricePerPandit: 2499, minPandits: 2, maxPandits: 4, defaultPandits: 2, duration: '1 day' },
        { key: 'madhyam', label: 'Madhyam Anusthan', basePricePerPandit: 3499, minPandits: 3, maxPandits: 6, defaultPandits: 3, duration: '2 days' },
        { key: 'maha', label: 'Maha Anusthan', basePricePerPandit: 4999, minPandits: 5, maxPandits: 9, defaultPandits: 5, duration: '3+ days' }
      ]
    },
    packageLabel: 'Shravan Anusthan Charges',
    packageDescription: 'Anusthan type and pandit count based transparent pricing.',
    platformFeeNote: 'Platform fee includes planning, support, and coordination.',
    execution: { pandits: '2-9 pandits', duration: '1 to 3+ days' },
    addOns: [{ id: 'jal-abhishek-addon', name: 'Jal Abhishek Add-on', description: 'Additional jal abhishek with guided sequence.', price: 999 }],
    detailedInfo: {
      highlights: [
        'Specially designed for Shravan/Sawan observances.',
        'Flexible by anusthan level and pandit team size.',
        'Great for family temples and community setups.'
      ]
    }
  },
  {
    key: 'wedding-flex-pricing',
    matchers: ['traditional hindu wedding', 'marriage ceremony', 'wedding'],
    platformFee: 299,
    dakshina: { min: 5000, max: 21000, step: 500, defaultValue: 11000 },
    packageLabel: 'Wedding Dakshina',
    packageDescription: 'Set dakshina based on ceremony scale and family preferences.',
    platformFeeNote: 'Platform fee includes dedicated planning assistance and vendor coordination support.',
    execution: { pandits: '2-3 pandits', duration: '4 to 6 hours' },
    addOns: [
      { id: 'wedding-samagri', name: 'Wedding Samagri Master Kit', description: 'Complete ceremony samagri managed end-to-end.', price: 2999 },
      { id: 'ritual-coordinator', name: 'Ritual Coordinator', description: 'On-ground coordinator for smooth sequence management.', price: 2499 }
    ]
  }
];

const resolveByMatchers = (searchText) => {
  return SERVICE_PRICING_CONFIGS.find((config) =>
    (config.matchers || []).some((matcher) => searchText.includes(normalizeText(matcher)))
  ) || null;
};

const toNumberPrice = (value) => {
  if (typeof value === 'number') {
    return value;
  }
  const parsed = parseInt(String(value || '').replace(/[^\d]/g, ''), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const computeDefaultPricing = (service = {}, config = null) => {
  if (!config) {
    const base = toNumberPrice(service?.price);
    return {
      baseLabel: 'Package Price',
      baseAmount: base,
      platformFee: 0,
      total: base,
      schedule: null,
      jaapSelection: null,
      anusthanSelection: null
    };
  }

  const platformFee = Number(config.platformFee || 0);

  if (config.schedulePricing?.mode === 'per-pandit-per-day') {
    const defaultDays = Number(config.schedulePricing.defaultDays || config.schedulePricing.dayOptions?.[0] || 1);
    const dayRule = config.schedulePricing.panditCountByDays?.[String(defaultDays)] || { defaultValue: 1 };
    const defaultPandits = Number(dayRule.defaultValue || dayRule.min || 1);
    const rate = Number(config.schedulePricing.ratePerPanditPerDay || 0);
    const baseAmount = rate * defaultDays * defaultPandits;

    return {
      baseLabel: 'Ritual Charges',
      baseAmount,
      platformFee,
      total: baseAmount + platformFee,
      schedule: {
        days: defaultDays,
        pandits: defaultPandits,
        ratePerPanditPerDay: rate
      },
      jaapSelection: null,
      anusthanSelection: null
    };
  }

  if (config.jaapPricing?.mode === 'jaap-count') {
    const defaultCount = Number(config.jaapPricing.defaultCount || config.jaapPricing.options?.[0]?.count || 0);
    const option = (config.jaapPricing.options || []).find((item) => Number(item.count) === defaultCount)
      || config.jaapPricing.options?.[0]
      || { count: defaultCount, label: `${defaultCount} Jaap`, price: 0, pandits: 'As per requirement', duration: 'To be confirmed' };
    const baseAmount = Number(option.price || 0);

    return {
      baseLabel: 'Jaap Charges',
      baseAmount,
      platformFee,
      total: baseAmount + platformFee,
      schedule: null,
      jaapSelection: {
        count: option.count,
        label: option.label,
        pandits: option.pandits,
        duration: option.duration
      },
      anusthanSelection: null
    };
  }

  if (config.anusthanPricing?.mode === 'anusthan-pandit') {
    const defaultTypeKey = config.anusthanPricing.defaultType || config.anusthanPricing.types?.[0]?.key;
    const type = (config.anusthanPricing.types || []).find((item) => item.key === defaultTypeKey)
      || config.anusthanPricing.types?.[0]
      || { label: 'Anusthan', basePricePerPandit: 0, defaultPandits: 1, duration: 'To be confirmed' };
    const pandits = Number(type.defaultPandits || type.minPandits || 1);
    const rate = Number(type.basePricePerPandit || 0);
    const baseAmount = rate * pandits;

    return {
      baseLabel: 'Anusthan Charges',
      baseAmount,
      platformFee,
      total: baseAmount + platformFee,
      schedule: null,
      jaapSelection: null,
      anusthanSelection: {
        type: type.label,
        pandits,
        ratePerPandit: rate,
        duration: type.duration
      }
    };
  }

  if (config.dakshina) {
    const baseAmount = Number(config.dakshina.defaultValue || 0);
    return {
      baseLabel: 'Dakshina',
      baseAmount,
      platformFee,
      total: baseAmount + platformFee,
      schedule: null,
      jaapSelection: null,
      anusthanSelection: null
    };
  }

  const baseAmount = toNumberPrice(service?.price);
  return {
    baseLabel: 'Package Price',
    baseAmount,
    platformFee,
    total: baseAmount + platformFee,
    schedule: null,
    jaapSelection: null,
    anusthanSelection: null
  };
};

export const getServiceDisplayPrice = (service = {}) => {
  const config = resolveServicePricingConfig(service);
  return computeDefaultPricing(service, config).total;
};

export const buildConfiguredBookingData = (service = {}, servicePage = 'home') => {
  const config = resolveServicePricingConfig(service);
  const pricing = computeDefaultPricing(service, config);
  const normalizedService = normalizeService(service, { servicePage });

  const packageInfo = {
    name: config?.packageLabel || normalizedService.title,
    price: pricing.baseAmount,
    duration: normalizedService.duration || config?.execution?.duration,
    description: config?.packageDescription || normalizedService.description
  };

  return {
    service: normalizedService,
    package: packageInfo,
    addOns: [],
    totalPrice: pricing.total,
    pricing: {
      baseLabel: pricing.baseLabel,
      dakshina: pricing.baseAmount,
      platformFee: pricing.platformFee,
      addOns: 0,
      total: pricing.total,
      schedule: pricing.schedule,
      jaapSelection: pricing.jaapSelection,
      anusthanSelection: pricing.anusthanSelection,
      platformFeeNote: config?.platformFeeNote || ''
    },
    bookingMode: 'service',
    servicePage
  };
};

export const resolveServicePricingConfig = (service = {}) => {
  const searchText = buildServiceSearchText(service);
  const directMatch = resolveByMatchers(searchText);
  if (directMatch) {
    return directMatch;
  }

  const catalogEntry = resolveServiceCatalogEntry(service);
  if (!catalogEntry) {
    return null;
  }

  const catalogSearchText = normalizeText(`${catalogEntry.title} ${(catalogEntry.aliases || []).join(' ')}`);
  return resolveByMatchers(catalogSearchText);
};
